import React from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import ScrollingBanner from '@/components/ScrollingBanner';
import NewsTicker from '@/components/NewsTicker';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Sprout, BookOpen, HandHeart, Globe2, Building2, Sparkles, Coins } from 'lucide-react';

const VISION_DIMENSIONS = [
  {
    id: 'social',
    label: 'Social',
    icon: HandHeart,
    color: '#4A6B48',
    text: "Cohésion communautaire, solidarité agissante, dignité des plus vulnérables.",
  },
  {
    id: 'economic',
    label: 'Économique',
    icon: Coins,
    color: '#D87D4A',
    text: "Autonomie des ménages paysans, crédits rotatifs solidaires, économie réelle, production de la richesse pour l'autonomisation des ménages (femmes, filles) et promotion des crédits rotatifs solidaires.",
  },
  {
    id: 'political',
    label: 'Politique',
    icon: Building2,
    color: '#4A6B48',
    text: "Citoyenneté participative, gouvernance locale, plaidoyer pour des politiques justes et la souveraineté alimentaire.",
  },
  {
    id: 'cultural',
    label: 'Culturel',
    icon: Globe2,
    color: '#D87D4A',
    text: "Valorisation des savoirs locaux, langues, traditions et patrimoine immatériel.",
  },
  {
    id: 'spiritual',
    label: 'Spirituel',
    icon: Sparkles,
    color: '#4A6B48',
    text: "Conscience collective, valeurs éthiques au cœur de l'action.",
  },
];

const STRATEGIC_AXES = [
  { id: 'agriculture', title: 'Production Agricole', description: "Agriculture et élevage avec approche agroforestière", icon: Sprout, color: '#4A6B48' },
  { id: 'training', title: 'Formation FPT', description: "Sensibilisation conscientisante Former Pour Transformer", icon: BookOpen, color: '#D87D4A' },
  { id: 'networking', title: 'Réseautage', description: "Création de réseaux et partenariats stratégiques", icon: Users, color: '#4A6B48' },
  { id: 'evaluation', title: 'Évaluation', description: "Évaluation participative permanente", icon: BookOpen, color: '#D87D4A' },
];

export default function HomePage() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <ScrollingBanner />
      <NewsTicker />

      {/* Hero Section */}
      <section
        className="hero-section relative"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_ilot-communautaire/artifacts/sbtptw5v_original_e7821ca2-aebb-44f2-9d5c-7d4fa6cd3790_IMG_20240308_122027_758.jpg)`
        }}
        data-testid="hero-section"
      >
        <div className="container mx-auto px-6">
          <div className="hero-content max-w-3xl">
            <span className="inline-block bg-[#D87D4A] text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-3" data-testid="hero-kicker">
              CRESA ASBL
            </span>
            <p className="text-sm text-white font-medium mb-3 leading-relaxed max-w-2xl">
              pilote un nouveau paradigme de développement communautaire au Sud-Kivu, Haut-Katanga,
              Kinshasa et Kongo Central. Découvrez notre modèle innovant d&apos;îlot de Bushumba.
            </p>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="hero-title"
            >
              Îlots de Développement Intégral Auto-gérés en RD Congo
            </h1>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/projets" className="btn-primary inline-flex items-center justify-center" data-testid="hero-cta-projects">
                Découvrir nos projets
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link to="/dons" className="btn-secondary inline-flex items-center justify-center" data-testid="hero-cta-donate">
                Faire un don
                <Heart className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </div>
        <div
          className="absolute bottom-0 left-0 right-0 bg-black/55 text-white/95 text-[11px] sm:text-xs px-6 py-1.5 italic text-center backdrop-blur-sm"
          data-testid="hero-caption"
        >
          📷 J.I.F 2023 à Bushumba : avec les mamans sachets noirs à la tête dénoncent les violences faites aux paysannes.
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding bg-white" data-testid="about-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#2E332D] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Découvrir CRESA ASBL
            </h2>
            <p className="text-base text-[#5C665A] max-w-3xl mx-auto leading-relaxed">
              Le CRESA ASBL (Baraza la Afia en swahili) est une organisation sans but lucratif créée en 2010
              autour de grands questionnements sur les problématiques de développement durable partant des
              questions d&apos;accès pour tous aux soins de santé. La santé est un droit fondamental et un des
              indicateurs clefs de développement pour toute société.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="card" data-testid="about-card-mission">
              <div className="flex items-center mb-4">
                <BookOpen className="text-[#4A6B48] mr-3" size={28} />
                <h3 className="text-xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Notre Mission
                </h3>
              </div>
              <p className="text-sm text-[#5C665A] leading-relaxed">
                Fédérer les communautés à travers les forces vives locales pour participer à l&apos;amélioration
                du bien-être général et coordonner les stratégies de développement.
              </p>
            </div>
            <div className="card" data-testid="about-card-approach">
              <div className="flex items-center mb-4">
                <Users className="text-[#D87D4A] mr-3" size={28} />
                <h3 className="text-xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Notre Approche
                </h3>
              </div>
              <p className="text-sm text-[#5C665A] leading-relaxed">
                &quot;Former Pour Transformer&quot; (FPT) — Une approche de sensibilisation conscientisante inspirée
                du pédagogue brésilien Paulo Freire pour un changement durable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision intégrale */}
      <section className="section-padding bg-[#F9F8F6]" data-testid="vision-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-10 max-w-3xl mx-auto">
            <span className="inline-block text-xs font-bold tracking-wider text-[#D87D4A] uppercase mb-3">
              Notre vision
            </span>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#2E332D] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Un développement intégral fondé sur la notion du bien commun de l&apos;humanité
            </h2>
            <p className="text-base text-[#5C665A] leading-relaxed">
              Pour CRESA ASBL, le développement est <strong className="text-[#2E332D]">intégral</strong> :
              social, économique, politique, culturel, spirituel… Notre vision est basée sur la
              <strong className="text-[#2E332D]"> notion du bien commun de l&apos;humanité </strong>
              notamment la terre, l&apos;eau, la santé, l&apos;éducation, la paix et la dignité.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            {VISION_DIMENSIONS.map((d) => (
              <div key={d.id} className="card text-center hover:-translate-y-1 transition-transform" data-testid={`vision-dim-${d.id}`}>
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: `${d.color}1A` }}>
                  <d.icon style={{ color: d.color }} size={28} />
                </div>
                <h3 className="text-lg font-bold text-[#2E332D] mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {d.label}
                </h3>
                <p className="text-xs text-[#5C665A] leading-relaxed">{d.text}</p>
              </div>
            ))}
          </div>

          <div className="bg-white border-l-4 border-[#4A6B48] rounded-lg p-6 max-w-3xl mx-auto">
            <p className="text-sm text-[#2E332D] leading-relaxed italic">
              « Nous croyons que la propulsion d&apos;îlots de développement intégral auto-gérés constitue le seul plan
              crédible pour la sortie des impasses qui freinent l&apos;envol des pays africains. C&apos;est l&apos;humain entier,
              et non un segment de l&apos;humain, que nous travaillons à faire grandir. »
            </p>
          </div>
        </div>
      </section>

      {/* Strategic Axes */}
      <section className="section-padding bg-white" data-testid="axes-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-10">
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#2E332D] mb-3"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Nos 4 axes stratégiques
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STRATEGIC_AXES.map((a) => (
              <div key={a.id} className="card text-center" data-testid={`axis-card-${a.id}`}>
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: `${a.color}1A` }}>
                  <a.icon style={{ color: a.color }} size={26} />
                </div>
                <h3 className="text-base font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {a.title}
                </h3>
                <p className="text-xs text-[#5C665A]">{a.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#F9F8F6]" data-testid="cta-section">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <h2 className="text-3xl font-bold text-[#2E332D] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Rejoignez ou soutenez le mouvement
          </h2>
          <p className="text-sm text-[#5C665A] mb-6">
            Découvrez comment vous pouvez contribuer à la mise en route des îlots de développement auto-gérés en RD Congo.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" className="btn-primary inline-flex items-center justify-center" data-testid="cta-join-button">
              <Users className="mr-2" size={18} /> Devenir membre
            </Link>
            <Link to="/dons" className="btn-secondary inline-flex items-center justify-center" data-testid="cta-donate-button">
              <Heart className="mr-2" size={18} /> Soutenir financièrement
            </Link>
            <Link to="/formations" className="inline-flex items-center justify-center px-6 py-3 rounded-full font-medium border-2 border-[#4A6B48] text-[#4A6B48] hover:bg-[#4A6B48] hover:text-white transition-all">
              <BookOpen className="mr-2" size={18} /> Postuler à une formation
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
